import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import QRCode from 'qrcode';

import './AfficheQrLieu.css';

/**
 * Affiche imprimable du QR d'un lieu de travail (C13 étendu et C25,
 * Yros/Kavero#461).
 *
 * Le QR encode une URL du portail, pas le jeton brut : le salarié le lit avec
 * l'appareil photo de son téléphone, qui ouvre l'écran de pointage. Il est
 * calculé ici, sans réseau — la librairie ne rend que la matrice, et c'est ce
 * composant qui la trace en SVG : net à l'impression, quelle que soit la
 * taille de la feuille.
 *
 * Tout ce qui s'imprime est dans `.affiche-qr-feuille` ; les boutons sont
 * dehors. La feuille de style masque le reste de la page à l'impression.
 */

// Marge blanche autour du code, en modules : la norme en demande quatre pour
// qu'un lecteur le détache de ce qui l'entoure.
const MARGE = 4;

export const urlDePointage = (jeton) =>
  `${window.location.origin}/espace/pointage?lieu=${encodeURIComponent(jeton)}`;

// Un tracé unique plutôt qu'un rectangle par module : un QR de cette taille en
// compte plus de mille.
const traceDuQr = (contenu) => {
  const { modules } = QRCode.create(contenu, { errorCorrectionLevel: 'M' });
  let trace = '';
  for (let ligne = 0; ligne < modules.size; ligne += 1) {
    for (let colonne = 0; colonne < modules.size; colonne += 1) {
      if (modules.get(ligne, colonne)) {
        trace += `M${colonne + MARGE} ${ligne + MARGE}h1v1h-1z`;
      }
    }
  }
  return { trace, cote: modules.size + 2 * MARGE };
};

function AfficheQrLieu({ lieu, societe, regeneration, onRegenerer, onFermer }) {
  const { t } = useTranslation();
  // Confirmation en deux temps : régénérer rend caduques les affiches déjà
  // imprimées, ce n'est pas un clic qu'on rattrape.
  const [confirmation, setConfirmation] = useState(false);
  const contenu = urlDePointage(lieu.jeton_qr);
  const { trace, cote } = useMemo(() => traceDuQr(contenu), [contenu]);
  const enCours = regeneration?.etat === 'encours';

  const confirmer = async () => {
    await onRegenerer();
    setConfirmation(false);
  };

  return (
    <div
      data-testid="affiche-qr"
      role="dialog"
      aria-modal="true"
      aria-label={t('configurationPointage.afficheTitre')}
      className="affiche-qr fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-slate-900/50 p-4"
    >
      <div className="affiche-qr-cadre w-full max-w-md rounded-2xl bg-white p-5 shadow-xl">
        <div data-testid="affiche-qr-feuille" className="affiche-qr-feuille text-center">
          {societe && (
            <p data-testid="affiche-qr-societe" className="text-sm font-medium uppercase tracking-wide text-slate-500">
              {societe}
            </p>
          )}
          <p data-testid="affiche-qr-lieu" className="mt-1 text-2xl font-bold text-slate-800">
            {lieu.nom}
          </p>
          <svg
            data-testid="affiche-qr-code"
            data-contenu={contenu}
            role="img"
            aria-label={t('configurationPointage.afficheTitre')}
            viewBox={`0 0 ${cote} ${cote}`}
            shapeRendering="crispEdges"
            className="affiche-qr-code mx-auto mt-4 w-64 max-w-full"
          >
            <rect width={cote} height={cote} fill="#ffffff" />
            <path data-testid="affiche-qr-trace" d={trace} fill="#000000" />
          </svg>
          <p className="mt-4 text-base font-medium text-slate-700">
            {t('configurationPointage.afficheConsigne')}
          </p>
        </div>

        <div data-testid="affiche-qr-actions" className="affiche-qr-hors-impression mt-5 space-y-3">
          {regeneration?.etat === 'succes' && (
            <p data-testid="affiche-qr-regenere" className="rounded-lg border border-green-200 bg-green-100 px-3 py-2 text-sm text-green-800">
              {t('configurationPointage.regenererSucces')}
            </p>
          )}
          {regeneration?.etat === 'erreur' && (
            <p data-testid="affiche-qr-erreur" className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              {regeneration.message}
            </p>
          )}

          {confirmation ? (
            <div data-testid="affiche-qr-confirmation" className="space-y-2 rounded-lg bg-yellow-100 px-3 py-2 text-sm text-yellow-800">
              <p>{t('configurationPointage.regenererAvertissement')}</p>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  data-testid="affiche-qr-regenerer-confirmer"
                  onClick={confirmer}
                  disabled={enCours}
                  className="px-3 py-1.5 text-xs font-medium rounded-lg border border-red-200 bg-white text-red-700 hover:bg-red-50 disabled:opacity-50"
                >
                  {enCours ? t('configurationPointage.regenererEnCours') : t('configurationPointage.regenererConfirmer')}
                </button>
                <button
                  type="button"
                  data-testid="affiche-qr-regenerer-renoncer"
                  onClick={() => setConfirmation(false)}
                  disabled={enCours}
                  className="px-3 py-1.5 text-xs font-medium rounded-lg text-slate-600 hover:bg-white disabled:opacity-50"
                >
                  {t('configurationPointage.regenererRenoncer')}
                </button>
              </div>
            </div>
          ) : (
            <div className="flex flex-wrap items-center justify-between gap-2">
              <button
                type="button"
                data-testid="affiche-qr-regenerer"
                onClick={() => setConfirmation(true)}
                className="px-3 py-2 text-xs font-medium rounded-lg border border-slate-200 text-slate-700 hover:bg-slate-50"
              >
                {t('configurationPointage.regenerer')}
              </button>
              <div className="flex gap-2">
                <button
                  type="button"
                  data-testid="affiche-qr-fermer"
                  onClick={onFermer}
                  className="px-3 py-2 text-xs font-medium rounded-lg text-slate-600 hover:bg-slate-50"
                >
                  {t('configurationPointage.fermer')}
                </button>
                <button
                  type="button"
                  data-testid="affiche-qr-imprimer"
                  onClick={() => window.print()}
                  className="px-4 py-2 text-sm font-medium rounded-lg bg-[--color-primary] text-white hover:opacity-90"
                >
                  {t('configurationPointage.imprimer')}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default AfficheQrLieu;
